// Reading the content assigned to a spot: which record answers for an id, and what image it shows.
//
// Both questions were answered privately inside SceneTree.tsx, and that is why the segment panel's
// Frames grid answered neither — the code existed one component over and was not reachable, so the
// grid drew empty tiles beside a Spots list that had the pictures.
//
// The precedence in getSpotThumbnailUrl is the reason this must be one function rather than two
// copies: it is not guessable. A video spot shows its *preview* image while an image spot shows its
// own media, the English localisation overrides both, and a QR spot has no thumbnail at all. A second
// implementation would agree with this one on the common case and diverge on the other three.

/** Media fields a spot record carries, both on the record itself and per language. */
export type SpotMedia = {
  url?: string;
  location?: string;
  previewImage?: string;
};

/**
 * A spot row as either list endpoint delivers it — `brandingSpots` or the room's own `roomSpots`.
 *
 * Deliberately the loose form. Both lists cross HTTP as `IBrandingSpotApi`, whose own declaration
 * ends in `[key: string]: unknown`, and the two callers hold them under different local aliases;
 * narrowing per field inside the readers keeps every call site cast-free.
 */
export type SpotRecord = Record<string, unknown>;

/** The content types a spot can carry, as the customization state and Unreal both name them. */
export type SpotContentType = 'IMAGE' | 'VIDEO' | 'VIDEO_STATIC' | 'QR';

export const DEFAULT_LANGUAGE_CODE = 'en';

/**
 * Trims a spot id for comparison, and does nothing else to it.
 *
 * Case is left exactly as it arrived, and that is load-bearing rather than incidental. Unreal is
 * asymmetric about it, which is worse than either extreme would be: content resolves through
 * `FString`-keyed `TMap`/`TSet` lookups, and those hash case-**in**sensitively, so a case-folded id
 * still paints its picture — while `FindSpotById`, which `BLINK_SPOT` and `TELEPORT_TO_SPOT` resolve
 * through, compares with an explicit `ESearchCase::CaseSensitive` and does not forgive it.
 *
 * So folding case here would not fail loudly. It would hang the picture correctly and quietly break
 * *show me this frame*, which is the hardest of the three outcomes to trace back to a `toLowerCase`.
 *
 * @param value - Anything an id might arrive as; a non-string is no id.
 * @returns The id, trimmed, or empty when there is none.
 */
export const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * Reads a spot's content type as the customization state spells it.
 *
 * Separate from `getSpotType`, which collapses `VIDEO_STATIC` into `IMAGE` because the scene tree
 * only picks an icon with it. Here the value decides which upload control the inspector opens, so
 * the fourth type has to survive.
 *
 * @param raw - The `type` field, off a spot record or off a geometry report's frame.
 * @returns The type, defaulting to `IMAGE` for anything unrecognised — the same default Unreal's
 * own selection message is read with.
 */
export const normalizeSpotContentType = (raw: unknown): SpotContentType => {
  if (typeof raw !== 'string') {
    return 'IMAGE';
  }

  const normalized = raw.toUpperCase();

  if (normalized === 'VIDEO') {
    return 'VIDEO';
  }

  if (normalized === 'VIDEO_STATIC') {
    return 'VIDEO_STATIC';
  }

  if (normalized === 'QR') {
    return 'QR';
  }

  return 'IMAGE';
};

/**
 * The image a spot is currently showing.
 *
 * Image spots resolve to their content URL; video spots fall back to their preview image; QR spots
 * have no thumbnail. Mirrors SpotInspector's own field precedence.
 *
 * @param spot - The spot's record, or null for an id no record answers for — which has no image
 * rather than a missing one.
 * @returns The URL, or null when this spot shows nothing.
 */
export const getSpotThumbnailUrl = (spot: SpotRecord | null): string | null => {
  if (!spot) {
    return null;
  }

  const localizations = spot.localizations as
    | Record<string, SpotMedia | undefined>
    | undefined;
  const english = localizations?.[DEFAULT_LANGUAGE_CODE];
  const data = spot.data as SpotMedia | undefined;
  const previewImage =
    english?.previewImage ||
    data?.previewImage ||
    (typeof spot.previewImage === 'string' ? spot.previewImage : undefined);
  const type = normalizeSpotContentType(spot.type);

  if (type === 'QR') {
    return null;
  }

  if (type === 'VIDEO') {
    return previewImage || null;
  }

  return (
    english?.url ||
    english?.location ||
    data?.url ||
    data?.location ||
    previewImage ||
    null
  );
};

/**
 * Resolves the photo of how the spot looks on the location.
 *
 * Unlike {@link getSpotThumbnailUrl} this is not the media the spot displays — it is the world
 * screenshot published by the UE editor script, or the one an admin uploaded in the CMS. Room spots
 * inherit it from the map spot and branding spots get it joined on read, so both list sources
 * render the same.
 *
 * @param spot - Spot row as delivered by the room or branding spots endpoint.
 * @returns The photo URL, or null when the spot has none.
 */
export const getSpotLocationPreviewUrl = (
  spot: SpotRecord | null,
): string | null => {
  const locationPreview = spot?.locationPreview as SpotMedia | null | undefined;

  return locationPreview?.url || locationPreview?.location || null;
};

/**
 * The record answering for a spot id, out of whichever list the editor is reading.
 *
 * An empty id matches nothing, and that is the point rather than a formality: Unreal reports an
 * empty `spotId` for a frame it cannot address individually, and a record whose `ueIdentifier` is
 * absent normalises to empty too. Matching those two would put one unrelated spot's picture on
 * every unaddressable frame in the layout.
 *
 * @param spots - The list to search — `brandingSpots` or `roomSpots`, either of which can be
 * absent before the editor has loaded one.
 * @param ueIdentifier - The id to answer for.
 * @returns The record, or null when the list has none for this id.
 */
export const findSpotByUeIdentifier = (
  spots: unknown,
  ueIdentifier: string,
): SpotRecord | null => {
  const wanted = normalizeUeIdentifier(ueIdentifier);

  if (!wanted || !Array.isArray(spots)) {
    return null;
  }

  return (
    (spots as SpotRecord[]).find(
      (spot) => normalizeUeIdentifier(spot?.ueIdentifier) === wanted,
    ) ?? null
  );
};
